import {
  STEP_NAMES,
  type RunDetail,
  type RunStepRow,
  type StepName,
  type StepStatus,
} from "@shared/types/runs";
import { FORCE_RESET_ERROR_PREFIX } from "@shared/runs/forceReset";

/**
 * Which step "Retry from the failed step" re-enqueues, and why the action is disabled when it
 * cannot (FR-54, Session 7 O4). Pure, so every rule below is tested without a screen.
 *
 * The rules mirror the server's refusals in `RunService.applyRetry`, so the page never offers a
 * retry the server would refuse for a reason the page already knew:
 *
 * - **One target per connection chain**: its earliest `failed` step. A retry resumes from the
 *   named step and re-opens the rest of that chain (and the run-level transform and finalize),
 *   so retrying the earliest failure is the whole of it.
 * - **The run-level transform only when no connection target exists**: a connection retry
 *   already re-opens a terminal transform, and retrying both would run dbt twice.
 * - **`finalize` only when nothing else qualifies**: it recomputes the run's status and lands
 *   nothing, so it is the target only for a run whose one failure is finalize itself.
 * - **Never `merge`**: nothing drives it yet, so the server answers 422.
 * - **Off while another run holds a connection of the run** (`heldByRunId`, #1200): the server
 *   answers 409 `RUN_IN_PROGRESS` until that run ends.
 * - **Never on a force-reset run.** A force reset writes rows and stops no container, so the
 *   server refuses every retry of such a run (409). The page reads the same two keys it can see:
 *   `summary.forceReset` and a step cancelled with the `Force reset: ` prefix. The one case the
 *   page cannot see (a reset that cancelled no step, after finalize replaced the summary) is
 *   answered by the server's 409, which the dialog shows inline.
 */

/** One step the dialog will re-enqueue. `dataflowId` is null for the run-level steps. */
export interface RetryTarget {
  step: StepName;
  dataflowId: string | null;
  row: RunStepRow;
}

export const RETRY_REASON_NO_FAILED_STEP =
  "No step failed. Start a new run to pick up the skipped table.";
export const RETRY_REASON_CANCELLED = "This run was cancelled. Start a new run.";
export const RETRY_REASON_FORCE_RESET = "A force reset stopped this run. Start a new run.";
/**
 * #1200: another run holds a connection of this run, so the server refuses the retry (409
 * `RUN_IN_PROGRESS`) until it ends. The list menu and the run page say so instead of offering it.
 * The same state reads "Import is off while an import runs." on Import now (`IMPORT_OFF_WHILE_RUNNING`,
 * `LastRunPanel.tsx`): a change to the words of one moves the other (`retryTargets.test.ts`).
 */
export const RETRY_REASON_CONNECTION_BUSY =
  "Retry is off while another import of this connection runs.";
/** The same, for a run of several connections: any one of them held is enough. */
export const RETRY_REASON_CONNECTIONS_BUSY =
  "Retry is off while another import of one of this run's connections runs.";
/** The server's own 422 sentence for a merge retry. */
export const RETRY_REASON_MERGE = "The merge step cannot be retried on its own yet.";

type RunLike = Pick<RunDetail, "status" | "summary"> &
  Partial<Pick<RunDetail, "heldByRunId" | "dataflowIds">>;

/**
 * The busy sentence for this run, or null when no other run holds a connection of it: "this
 * connection" for a run of one, "one of this run's connections" for a run of several (the server
 * holds a run when any one of its connections is in use).
 */
export function connectionBusyReason(
  run: Partial<Pick<RunLike, "heldByRunId" | "dataflowIds">>,
): string | null {
  if (!run.heldByRunId) return null;
  return (run.dataflowIds?.length ?? 0) > 1
    ? RETRY_REASON_CONNECTIONS_BUSY
    : RETRY_REASON_CONNECTION_BUSY;
}

/**
 * "No step failed" while another run holds a connection: a new run is refused too until that run
 * ends, so the sentence says to wait for it rather than send the person to a refusal.
 */
function noFailedStepWhileHeld(run: RunLike): string {
  const which =
    (run.dataflowIds?.length ?? 0) > 1 ? "one of this run's connections" : "this connection";
  return `No step failed. Once the other import of ${which} finishes, start a new run to pick up the skipped table.`;
}

function stepOrder(step: StepName): number {
  return STEP_NAMES.indexOf(step);
}

/** True when the run was force reset, as far as the page can tell (see the module comment). */
export function wasForceReset(run: Pick<RunDetail, "summary">, steps: RunStepRow[]): boolean {
  const summary = run.summary;
  if (summary !== null && typeof summary === "object" && "forceReset" in summary) return true;
  return steps.some(
    (row) => row.status === "cancelled" && Boolean(row.error?.startsWith(FORCE_RESET_ERROR_PREFIX)),
  );
}

/**
 * The steps "Retry from the failed step" re-enqueues, in the order it sends them.
 *
 * Empty for a force-reset run and for a cancelled run: both are "start a new run" (the reason
 * comes from `retryDisabledReason`).
 */
export function failedStepTargets(steps: RunStepRow[], run?: RunLike): RetryTarget[] {
  if (run?.status === "cancelled" || wasForceReset(run ?? { summary: null }, steps)) return [];

  const chains = new Map<string, RunStepRow[]>();
  for (const row of steps) {
    if (row.dataflowId === null) continue;
    const chain = chains.get(row.dataflowId) ?? [];
    chain.push(row);
    chains.set(row.dataflowId, chain);
  }

  const targets: RetryTarget[] = [];
  for (const [dataflowId, chain] of chains) {
    const earliest = chain
      .filter((row) => row.status === "failed" && row.step !== "merge" && row.step !== "finalize")
      .sort((a, b) => stepOrder(a.step) - stepOrder(b.step))[0];
    if (earliest) targets.push({ step: earliest.step, dataflowId, row: earliest });
  }
  if (targets.length > 0) return targets;

  const runLevel = (step: StepName) =>
    steps.find((row) => row.dataflowId === null && row.step === step && row.status === "failed");
  const transform = runLevel("transform");
  if (transform) return [{ step: "transform", dataflowId: null, row: transform }];
  const finalize = runLevel("finalize");
  if (finalize) return [{ step: "finalize", dataflowId: null, row: finalize }];
  return [];
}

/**
 * Why the action is disabled, or null when it is enabled (or absent: no `runs.retry` means the
 * page does not render it at all, so there is nothing to explain).
 */
export function retryDisabledReason(
  run: RunLike,
  steps: RunStepRow[],
  canRetry: boolean,
): string | null {
  if (!canRetry) return null;
  // The force reset wins over every other reason: a force-reset run can hold a `failed` step,
  // and "No step failed" would then be false as well as unhelpful.
  if (wasForceReset(run, steps)) return RETRY_REASON_FORCE_RESET;
  if (run.status === "cancelled") return RETRY_REASON_CANCELLED;
  // #1200: a retry the server would refuse for now, while another run holds the connection. The
  // permanent reasons above and below win: they stay true once that run ends.
  if (failedStepTargets(steps, run).length > 0) return connectionBusyReason(run);
  const failed = steps.filter((row) => row.status === "failed");
  if (failed.length > 0 && failed.every((row) => row.step === "merge")) return RETRY_REASON_MERGE;
  // A table retry is a new run as well, refused while the connection is held, so the page hides
  // it then: the sentence says why and when to start the new run (PR #1296 review).
  return run.heldByRunId ? noFailedStepWhileHeld(run) : RETRY_REASON_NO_FAILED_STEP;
}

/**
 * Whether one step's own "Retry the … step" button is offered.
 *
 * - never on a force-reset run (the same rule as the page's action);
 * - never for `merge`, whose retry the server refuses (422) because nothing drives it;
 * - `failed`: offered;
 * - `skipped`: only a load whose chain's extract succeeded (for example a load skipped for want
 *   of a destination binding). A load skipped because its extract failed has nothing staged,
 *   and the server refuses it (409);
 * - never `cancelled`, and never a transform skipped because no extract succeeded.
 */
export function stepRetryOffered(
  step: StepName,
  row: RunStepRow | undefined,
  chainExtractStatus: StepStatus | undefined,
  forceReset: boolean,
): boolean {
  if (forceReset || !row || step === "merge") return false;
  if (row.status === "failed") return true;
  if (row.status === "skipped") return step === "load" && chainExtractStatus === "succeeded";
  return false;
}
